import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withUtcDateFields } from '@application/data-access';
import {
  DeviceLoginStartRequest,
  DeviceLoginStartResponse,
} from '../entities/device-login.model';

/**
 * O servidor manda o `expiresIn` como instante UTC genuíno, com offset explícito
 * (`2026-08-19T17:00:41.6100922+00:00`), mas o nome não termina em `Utc` — a
 * regra do sufixo não o apanha. Declarado aqui, o `DateInterceptor` converte-o
 * na resposta; quem o lê a jusante compara-o contra `DateTime.now()`, que corre
 * no fuso da conta.
 *
 * Sem a conversão, em UTC-3 a expiração fica 3h no futuro — o temporizador de
 * renovação dispara tarde e o `isSessionStale()` dá um código morto como válido,
 * deixando um QR inútil no ecrã do quiosque. Em fuso positivo inverte: o código
 * nasce "expirado" e o `startSession` volta a pedir em ciclo.
 *
 * Um nome só chega para as duas grafias — `ExpiresIn` na rede, `expiresIn` no
 * modelo —, porque a comparação ignora maiúsculas/minúsculas.
 */
const DEVICE_LOGIN_UTC_DATE_FIELDS = ['ExpiresIn'] as const;

@Injectable({ providedIn: 'root' })
export class DeviceLoginService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_CONFIG);

  private get baseUrl(): string {
    return this.config.apiUrl.userService;
  }

  public start(
    payload: DeviceLoginStartRequest,
  ): Observable<DeviceLoginStartResponse> {
    return this.http.post<DeviceLoginStartResponse>(
      `${this.baseUrl}/DeviceLogin/Start`,
      payload,
      { context: withUtcDateFields(DEVICE_LOGIN_UTC_DATE_FIELDS) },
    );
  }

  public cancel(deviceCode: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/DeviceLogin/Cancel`, {
      deviceCode,
    });
  }

  public approve(activationCode: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/DeviceLogin/Approve`, {
      activationCode,
    }, {
      withCredentials: true
    });
  }
}
